import { i18nLoadPath } from "../multilang/i18n";
import { ID_PROCESS_CACHE_URLS, NAMED_CACHE_NAME, SW_EVENTS_TYPES_TO } from "./sw.constants";

import webFontConfig from "../../styles/egwWeFontConfig.json";
import { flagPlaceholder, makeLangImage, makePublicUrl } from "../url/url";
import { getBookIdNumber } from "../content/content";

export const registerServiceWorker = (scriptURL, onSuccessRegistration, onError, onFinally) => {
  if ("window" in global && "serviceWorker" in navigator) {
    navigator.serviceWorker.register(scriptURL).then(
      onSuccessRegistration,
      onError,
    ).finally(onFinally);
  }
};

export const unregisterServiceWorker = () => {
  if ("window" in global && "serviceWorker" in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (let registration of registrations) {
        registration.unregister();
      }
    });
  }
};

export const makeUrlsStaticForUsingAppOffline = (localeLang, themes, namespaces, langMap) => {
  const flagImages = Object.keys(langMap).map((code) => {
    return makePublicUrl(makeLangImage(code, true));
  });

  return [
    "/images/books/placeholders/base.png",
    "/images/books/placeholders/base-big.png",
    "/images/books/placeholders/apl.png",
    "/images/books/placeholders/apl-big.png",
    "/images/books/placeholders/bible.png",
    "/images/books/placeholders/bible-big.png",
    "/images/books/placeholders/reference.png",
    "/images/books/placeholders/reference-big.png",

    flagPlaceholder,
    ...flagImages,

    "/fonts/egw-web-font.ttf?" + webFontConfig.buildHash,
    "/fonts/egw-web-font.css",

    // FIXME: Add full list of fonts used in REACT app Reader.
    //  May be, use constant {fontOptions} and {window.getComputedStyle} to detect file of the font.
    "/fonts/stylesheet.css",
    "/fonts/BebasNeueBold.ttf",
    "/fonts/BebasNeueRegular.ttf",
    // ======================================================

    ...themes.map((theme) => "/styles/" + theme.id + ".css"),
    ...themes.map((theme) => "/images/" + theme.gifLoader),

    ...namespaces.map((ns) => i18nLoadPath(localeLang, ns)),
  ];
};

const postSWMessage = (type, data) => {
  if (!global.window || !navigator.serviceWorker) {
    return;
  }

  navigator.serviceWorker.ready.then((registration) => {
    if (registration.active) {
      registration.active.postMessage({
        type,
        data,
      });
    }
  });
};

export const postSWMessageCancelProcess = (processId) => {
  postSWMessage(SW_EVENTS_TYPES_TO.CANCEL_PROCESS, processId);
};

export const postSWMessageCacheUrls = (urls) => {
  postSWMessage(SW_EVENTS_TYPES_TO.CACHE_URLS, {processId: ID_PROCESS_CACHE_URLS, urls});
};

export const postSWMessageRemoveUrls = (urls) => {
  postSWMessage(SW_EVENTS_TYPES_TO.REMOVE_URLS, urls);
};

export const postSWMessageGetInitData = (urlsForOfflineUse) => {
  postSWMessage(SW_EVENTS_TYPES_TO.GET_INIT_DATA, urlsForOfflineUse);
};

export const postSWMessageAddToCache = (cacheName, key, value) => {
  postSWMessage(SW_EVENTS_TYPES_TO.ADD_TO_CACHE, {
    cacheName, key, value
  });
};

export const postSWMessageRemoveFromCache = (cacheName, key) => {
  postSWMessage(SW_EVENTS_TYPES_TO.REMOVE_FROM_CACHE, {
    cacheName,
    key,
  });
};

export const postSWMessageGetFromCache = (cacheName, key) => {
  postSWMessage(SW_EVENTS_TYPES_TO.GET_FROM_CACHE, {
    cacheName,
    key,
  });
};

export const postSWMessageClearNamedCaches = () => {
  postSWMessage(SW_EVENTS_TYPES_TO.CLEAR_NAMED_CACHES);
};

export const postSWMessageClearWebsiteData = () => {
  postSWMessage(SW_EVENTS_TYPES_TO.CLEAR_WEBSITE_DATA);
};

// Derivatives methods, for easier code reading.
/* === Book === */
export const postSWMessageCacheBook = (nBookId, bookData) => {
  postSWMessageAddToCache(NAMED_CACHE_NAME.BOOK_DATA, nBookId, bookData);
};

export const postSWMessageRemoveBook = (bookIdNumber) => {
  postSWMessageRemoveFromCache(NAMED_CACHE_NAME.BOOK_DATA, bookIdNumber);
};

export const postSWMessageGetBook = (bookIdNumber) => {
  postSWMessageGetFromCache(NAMED_CACHE_NAME.BOOK_DATA, bookIdNumber);
};

/* === Audio file === */
export const postSWMessageCacheAudioFile = (chapterId, base64) => {
  postSWMessageAddToCache(NAMED_CACHE_NAME.AUDIO_FILE, chapterId, base64);
};

export const postSWMessageRemoveAudio = (chapterId) => {
  postSWMessageRemoveFromCache(NAMED_CACHE_NAME.AUDIO_FILE, chapterId);
};

export const postSWMessageGetAudioFile = (chapterId) => {
  postSWMessageGetFromCache(NAMED_CACHE_NAME.AUDIO_FILE, chapterId);
};

/* === Waveform ===== */
export const postSWMessageCacheWaveform = (chapterId, data) => {
  postSWMessageAddToCache(NAMED_CACHE_NAME.WAVEFORM, chapterId, data);
};

export const postSWMessageRemoveWaveform = (chapterId) => {
  postSWMessageRemoveFromCache(NAMED_CACHE_NAME.WAVEFORM, chapterId);
};

export const postSWMessageGetWaveform = (chapterId) => {
  postSWMessageGetFromCache(NAMED_CACHE_NAME.WAVEFORM, chapterId);
};

/* === Offline history ===== */
export const postSWMessageCacheOfflineHistoryRead = (chapterId, paraId, saveML, timestamp) => {
  postSWMessageAddToCache(NAMED_CACHE_NAME.OFFLINE_HISTORY_READ, getBookIdNumber(chapterId), {
    chapterId, paraId, saveML, timestamp,
  });
};
export const postSWMessageCacheOfflineHistoryListen = (chapterId, position, saveML, timestamp) => {
  postSWMessageAddToCache(NAMED_CACHE_NAME.OFFLINE_HISTORY_LISTEN, getBookIdNumber(chapterId),{
    chapterId, position, saveML, timestamp,
  });
};

export const postSWMessageRemoveOfflineHistoryRead = (bookId) => {
  postSWMessageRemoveFromCache(NAMED_CACHE_NAME.OFFLINE_HISTORY_READ, bookId);
};
export const postSWMessageRemoveOfflineHistoryListen = (bookId) => {
  postSWMessageRemoveFromCache(NAMED_CACHE_NAME.OFFLINE_HISTORY_LISTEN, bookId);
};

export const postSWMessageGetOfflineHistoryRead = (bookId) => {
  postSWMessageGetFromCache(NAMED_CACHE_NAME.OFFLINE_HISTORY_READ, bookId);
};
export const postSWMessageGetOfflineHistoryListen = (bookId) => {
  postSWMessageGetFromCache(NAMED_CACHE_NAME.OFFLINE_HISTORY_LISTEN, bookId);
};
// ================================
